/**
 * Images API Service
 * Handles image upload, deletion, and metadata retrieval
 */

import { request } from './api';
import type { ApiResponse } from './api';

export type ImageUploadRequest = {
  file: File;
  entityType: 'ad' | 'wish' | 'member';
  entityId: string;
};

export type ImageUploadResponse = {
  key: string;
  url: string;
  size: number;
  contentType: string;
};

export type ImageMetadata = {
  key: string;
  url: string;
  entityType: 'ad' | 'wish' | 'member';
  entityId: string;
  fileName: string;
  size: number;
  contentType: string;
};

/**
 * Upload image to R2
 */
export async function uploadImage(
  params: ImageUploadRequest
): Promise<ApiResponse<ImageUploadResponse>> {
  const formData = new FormData();
  formData.append('file', params.file);
  formData.append('entityType', params.entityType);
  formData.append('entityId', params.entityId);

  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/images/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    },
    body: formData,
  });

  return response.json();
}

/**
 * Get image metadata
 */
export async function getImage(key: string): Promise<ApiResponse<ImageMetadata>> {
  return request<ImageMetadata>(`/api/images/${encodeURIComponent(key)}`);
}

/**
 * Delete image
 */
export async function deleteImage(key: string): Promise<ApiResponse<{ message: string; key: string }>> {
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/images/${encodeURIComponent(key)}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    },
  });

  return response.json();
}

/**
 * Upload multiple images
 */
export async function uploadImages(
  files: File[],
  entityType: 'ad' | 'wish' | 'member',
  entityId: string
): Promise<ImageUploadResponse[]> {
  const results = await Promise.allSettled(
    files.map((file) => uploadImage({ file, entityType, entityId }))
  );

  return results
    .filter((result): result is PromiseFulfilledResult<ApiResponse<ImageUploadResponse>> => 
      result.status === 'fulfilled' && result.value.success
    )
    .map((result) => result.value.data!);
}
